import { useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  ChevronLeft,
  Share,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroPhoto } from "@/components/editorial";
import { PHOTOS } from "@/lib/photos";
import { useMenuPlan } from "@/hooks/useMenuPlan";
import { useRecipeSteps } from "@/hooks/useRecipeSteps";
import { useFavorites } from "@/features/favorites/useFavorites";
import { findRecipeBySlug, useMenu } from "@/features/menu/store";
import { formatDayLong, nutriColor } from "@/features/menu/menuLabels";
import { cn } from "@/lib/utils";
import { FeedbackSheet } from "@/features/feedback/FeedbackSheet";
import type { Ingredient, Recipe } from "@/types/menu";

/** Loosely classifies ingredients as "already prepped Sunday" vs "to assemble". */
const READY_AISLES = new Set(["pantry", "frozen"]);

export function RecipeDetailPage() {
  const { slug = "" } = useParams();
  // `?feedback=1` is the click target of the feedback-reminder push
  // (backend services/notifications.py → public/sw.js).
  const [searchParams, setSearchParams] = useSearchParams();
  const { plan, isLoading } = useMenuPlan();
  const { isFavorite, toggle: toggleFav } = useFavorites();
  const [feedbackOpen, setFeedbackOpen] = useState(
    () => searchParams.get("feedback") === "1",
  );
  const onFeedbackOpenChange = (open: boolean) => {
    setFeedbackOpen(open);
    // Drop the push deep-link flag once handled so a reload or a back
    // navigation does not reopen the sheet.
    if (!open && searchParams.has("feedback")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("feedback");
          return next;
        },
        { replace: true },
      );
    }
  };

  if (isLoading && !plan) {
    return (
      <div className="grid h-full place-items-center text-ink-50">
        Chargement…
      </div>
    );
  }
  const recipe = findRecipeBySlug(plan, slug);
  if (!plan || !recipe) {
    return <Navigate to="/menu" replace />;
  }

  const dayInfo = plan.days.find((d) => d.meals.dinner?.slug === slug);
  const dayLabel = dayInfo ? formatDayLong(dayInfo.day) : null;
  const totalMin = recipe.prep_minutes + recipe.cook_minutes;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      {/* Single scroll area: hero → meta → summary → tabs → feedback */}
      <div className="flex-1 overflow-auto">
        {/* Photo hero */}
        <div className="relative h-[320px] overflow-hidden">
          <HeroPhoto src={PHOTOS.bowls} blur={0} scale={1.02} />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.35), transparent 40%, rgba(0,0,0,0.55))",
            }}
          />
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5">
            <Link
              to="/menu"
              aria-label="Retour au menu"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground backdrop-blur-sm"
            >
              <ChevronLeft className="size-4" />
            </Link>
            <div className="flex gap-2">
              <button
                aria-label={
                  isFavorite(slug)
                    ? "Retirer des favoris"
                    : "Ajouter aux favoris"
                }
                onClick={() => toggleFav(slug, "meal", recipe)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground backdrop-blur-sm"
              >
                <Heart
                  className="size-4"
                  fill={isFavorite(slug) ? "currentColor" : "none"}
                />
              </button>
              <button
                aria-label="Partager"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-foreground backdrop-blur-sm"
              >
                <Share className="size-4" />
              </button>
            </div>
          </div>
          <div className="absolute inset-x-6 bottom-5 text-background">
            {dayLabel && (
              <div className="mb-2.5 text-[10px] uppercase tracking-[0.22em] text-white/85">
                {dayLabel}
              </div>
            )}
            <h1 className="font-serif text-[32px] font-normal leading-[1] tracking-[-0.015em]">
              {recipe.title.split(",").map((part, i, arr) => (
                <span key={i} className={i === arr.length - 1 ? "italic" : ""}>
                  {part}
                  {i < arr.length - 1 ? "," : "."}
                  {i < arr.length - 1 ? <br /> : null}
                </span>
              ))}
            </h1>
          </div>
        </div>

        {/* Meta strip */}
        <div className="grid grid-cols-4 border-b border-hair bg-background">
          <Meta value={`${totalMin}'`} label="temps" />
          <Meta
            value={recipe.calories != null ? String(recipe.calories) : "—"}
            label="kcal"
          />
          <Meta
            value={recipe.nutri_score ?? "—"}
            label="nutri"
            pillColor={nutriColor(recipe.nutri_score)}
          />
          <Meta value={String(recipe.servings)} label="pers." last />
        </div>

        {/* Description */}
        {recipe.summary && (
          <div className="px-6 pt-6 font-serif text-[17px] font-light italic leading-relaxed text-ink-70">
            « {recipe.summary} »
          </div>
        )}

        {/* Segmented tabs — tab bar sticks to top of scroll area while scrolling */}
        <RecipeBody recipe={recipe} />

        {/* Feedback CTA — Phase 13b */}
        <div className="border-t border-hair px-6 py-4">
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-pill border border-hair bg-background px-4 py-3 text-[13px] text-ink-60 transition-colors hover:border-ink-30 hover:text-foreground"
          >
            <MessageSquare className="size-4" />
            Tu as cuisiné ce plat ? Donne ton avis
          </button>
        </div>
      </div>

      {/* Action CTAs — pinned at bottom, always visible */}
      <div className="shrink-0 flex flex-col gap-2 border-t border-hair bg-background px-6 py-5">
        <Button asChild size="lg" className="w-full">
          <Link to={`/cooking?recipe=${encodeURIComponent(recipe.slug)}`}>
            Cuisiner ce repas <ArrowRight />
          </Link>
        </Button>
        <Button asChild size="sm" variant="ghost" className="w-full">
          <Link to="/cooking">Mode batch dimanche</Link>
        </Button>
      </div>

      {/* Feedback sheet */}
      <FeedbackSheet
        open={feedbackOpen}
        onOpenChange={onFeedbackOpenChange}
        recipeSlug={slug}
        recipeTitle={recipe.title}
      />
    </div>
  );
}

function RecipeBody({ recipe }: { recipe: Recipe }) {
  const [tab, setTab] = useState<"ingredients" | "steps" | "nutrition">(
    "ingredients",
  );
  const plan = useMenu((s) => s.plan);
  const { isLoading, error } = useRecipeSteps(recipe, plan);
  return (
    <div>
      <div className="sticky top-0 z-10 flex gap-1 border-b border-hair bg-background px-6 pb-1 pt-6">
        {(
          [
            ["ingredients", "Ingrédients"],
            ["steps", "Étapes"],
            ["nutrition", "Nutrition"],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              "-mb-px border-b-2 px-3.5 py-2.5 text-[13px] transition-colors",
              tab === k
                ? "border-foreground font-semibold text-foreground"
                : "border-transparent text-ink-50",
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "ingredients" && <IngredientsTab recipe={recipe} />}
      {tab === "steps" && (
        <StepsTab recipe={recipe} isLoading={isLoading} error={error} />
      )}
      {tab === "nutrition" && <NutritionTab recipe={recipe} />}
    </div>
  );
}

function IngredientsTab({ recipe }: { recipe: Recipe }) {
  const shopping = useMenu((s) => s.plan?.shopping_list ?? null);

  // name|unit → total batch quantity (aggregated across all recipes this week)
  const batchTotals = useMemo(() => {
    const map = new Map<string, number>();
    if (!shopping) return map;
    for (const items of Object.values(shopping.items_by_aisle)) {
      for (const item of items ?? []) {
        const key = `${item.name.toLowerCase().trim()}|${item.unit.toLowerCase().trim()}`;
        map.set(key, item.quantity);
      }
    }
    return map;
  }, [shopping]);

  const ready = recipe.ingredients.filter((i) => READY_AISLES.has(i.aisle));
  const toAssemble = recipe.ingredients.filter(
    (i) => !READY_AISLES.has(i.aisle),
  );
  return (
    <div className="px-6 pb-6 pt-5">
      {ready.length > 0 && (
        <IngredientGroup
          title="⌐ Préparé dimanche"
          items={ready}
          ready
          batchTotals={batchTotals}
        />
      )}
      {toAssemble.length > 0 && (
        <IngredientGroup
          title="+ À assembler"
          items={toAssemble}
          ready={false}
          batchTotals={batchTotals}
        />
      )}
    </div>
  );
}

function IngredientGroup({
  title,
  items,
  ready,
  batchTotals,
}: {
  title: string;
  items: Ingredient[];
  ready: boolean;
  batchTotals: Map<string, number>;
}) {
  return (
    <div className="mb-2">
      <div className="mb-3.5 mt-5 text-[10px] uppercase tracking-[0.2em] text-ink-50">
        {title}
      </div>
      {items.map((i, idx) => {
        const key = `${i.name.toLowerCase().trim()}|${i.unit.toLowerCase().trim()}`;
        const batchTotal = batchTotals.get(key);
        // Show batch ratio only when multiple recipes share this ingredient
        const showBatch = batchTotal != null && batchTotal > i.quantity * 1.001;

        return (
          <div
            key={`${i.name}-${idx}`}
            className={cn(
              "flex items-center justify-between border-b border-hair py-3",
              idx === items.length - 1 && "border-b-0",
            )}
          >
            <div className="flex items-center gap-3">
              {ready ? (
                <span className="text-accent">✓</span>
              ) : (
                <span className="h-[14px] w-[14px] shrink-0 border-[1.5px] border-ink-30" />
              )}
              <span
                className={cn(
                  "font-serif text-[16px]",
                  ready && "italic text-ink-70",
                )}
              >
                {i.name}
              </span>
            </div>
            <div className="text-right">
              <div className="font-mono text-[11px] text-ink-50">
                {formatQuantity(i.quantity)} {i.unit}
              </div>
              {showBatch && (
                <div className="font-mono text-[10px] text-ink-30">
                  sur {formatQuantity(batchTotal!)} {i.unit}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepsTab({
  recipe,
  isLoading,
  error,
}: {
  recipe: Recipe | null;
  isLoading: boolean;
  error: string | null;
}) {
  if (isLoading) {
    return (
      <div className="px-6 py-8 text-center text-[13px] text-ink-50">
        Chargement des étapes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8 text-center text-[13px] text-ink-50">
        <p>Erreur : {error}</p>
        <p className="mt-2">Revenez plus tard ou réessayez.</p>
      </div>
    );
  }

  const steps = recipe?.prep_steps ?? [];

  if (steps.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-[13px] text-ink-50">
        Les étapes apparaîtront ici. Pour la cuisine pas-à-pas, utilisez le{" "}
        <Link to="/cooking" className="text-accent">
          Mode cuisine
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 pt-5">
      {steps.map((step) => (
        <div
          key={step.order}
          className="flex gap-3 border-b border-hair py-3 last:border-b-0"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[11px] font-mono text-accent">
            {step.order}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-[15px] leading-snug">
              <span className="font-semibold">{step.verb}</span>
              <span> {step.target}</span>
              {step.quantity && (
                <span className="text-ink-50"> — {step.quantity}</span>
              )}
            </div>
            {step.timer_seconds != null && (
              <div className="mt-1 text-[11px] text-accent font-mono">
                ⏱ {Math.floor(step.timer_seconds / 60)}:
                {(step.timer_seconds % 60).toString().padStart(2, "0")}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function NutritionTab({ recipe }: { recipe: Recipe }) {
  return (
    <div className="px-6 py-6">
      <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-ink-50">
        Par portion
      </div>
      <div className="grid grid-cols-2 gap-3">
        <NutritionCell
          label="Énergie"
          value={recipe.calories != null ? `${recipe.calories} kcal` : "—"}
        />
        <NutritionCell
          label="Protéines"
          value={formatGrams(recipe.protein_g)}
        />
        <NutritionCell label="Glucides" value={formatGrams(recipe.carbs_g)} />
        <NutritionCell label="Lipides" value={formatGrams(recipe.fat_g)} />
        <NutritionCell label="Fibres" value={formatGrams(recipe.fiber_g)} />
      </div>
      <p className="mt-6 text-[11px] text-ink-50">
        Estimations à titre indicatif — données{" "}
        <span className="italic">Nutri-Score {recipe.nutri_score ?? "—"}</span>{" "}
        et <span className="italic">Éco-Score {recipe.eco_score ?? "—"}</span>{" "}
        croisées avec Open Food Facts.
      </p>
    </div>
  );
}

/** Format a per-serving macro value in grams, or "—" when unavailable. */
function formatGrams(value: number | null): string {
  if (value == null) return "—";
  return `${formatQuantity(value)} g`;
}

function NutritionCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-hair px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-50">
        {label}
      </div>
      <div className="mt-1 font-serif text-xl">{value}</div>
    </div>
  );
}

function Meta({
  value,
  label,
  pillColor,
  last,
}: {
  value: string;
  label: string;
  pillColor?: string;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-4",
        !last && "border-r border-hair",
      )}
    >
      {pillColor ? (
        <span
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold text-background",
            pillColor,
          )}
        >
          {value}
        </span>
      ) : (
        <div className="font-serif text-[22px] font-normal tracking-[-0.005em]">
          {value}
        </div>
      )}
      <div className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-ink-50">
        {label}
      </div>
    </div>
  );
}

function formatQuantity(q: number): string {
  if (Number.isInteger(q)) return String(q);
  return q.toFixed(1).replace(".", ",");
}
